import * as React from "react"
import { authorizedFetch } from "../config/auth_axios"
import { APIHost } from "../config/api"

// バックエンドの認証必須エンドポイント。API レスポンスはこのパスの URL を返し、
// 直接開ける Active Storage の URL は一切含まない。
// ここを認証付き(XXX-Authorize ヘッダ)で叩くとファイルの中身(bytes)が返るので、
// それを blob: の object URL に変換して描画に使う。
// 未認証ではこのエンドポイントが 401 を返し、object URL も作れないため
// 画像・PDF・ファイルを一切参照できない。
const ATTACHMENT_PATH = "/api/v1/attachments/"

// endpoint URL -> 生成済み object URL（blob:）。
// blob: は元ファイルが変わらない限り有効なので TTL 不要。blob id が変われば
// endpoint URL も変わる（= 別キー）ため、URL をキーにキャッシュすれば十分。
const cache: Map<string, string> = new Map()
const inflight: Map<string, Promise<string>> = new Map()

const isAttachmentEndpoint = (src: string | undefined | null): boolean =>
  typeof src === "string" && src.indexOf(ATTACHMENT_PATH) >= 0

// The backend embeds an absolute URL via default_url_options[:host], but that
// host may not match the API host the browser can actually reach (APIHost) --
// especially in local dev (backend=localhost:3000 / frontend=APIHost). As a
// result <img>/fetch hits a non-existent host and fails with
// ERR_CONNECTION_REFUSED. So we slice off everything from the attachment path
// onward and re-anchor it to the same APIHost every other API call uses.
const normalizeAttachmentUrl = (src: string): string => {
  const idx = src.indexOf(ATTACHMENT_PATH)
  if (idx < 0) {
    return src
  }
  return APIHost + src.substring(idx)
}

/**
 * 添付ファイル参照エンドポイント (/api/v1/attachments/:id) を認証付きで叩き、
 * 返ってきた bytes を blob: の object URL に変換して返す。
 * 添付エンドポイント以外（Firebase / blob: / data: / 空文字）はそのまま返すため、
 * ローカルプレビュー URL 等に対しても安全に使える。
 */
export const resolveAttachmentUrl = (src: string | undefined | null): Promise<string> => {
  if (!src || !isAttachmentEndpoint(src)) {
    return Promise.resolve(src || "")
  }
  const key = normalizeAttachmentUrl(src)
  const cached = cache.get(key)
  if (cached) {
    return Promise.resolve(cached)
  }
  const existing = inflight.get(key)
  if (existing) {
    return existing
  }
  const p = authorizedFetch
    .get(key, { responseType: "blob" })
    .then((res: any) => {
      const objectUrl: string = (window.URL || (window as any).webkitURL).createObjectURL(res.data)
      cache.set(key, objectUrl)
      inflight.delete(key)
      return objectUrl
    })
    .catch((e: any) => {
      inflight.delete(key)
      throw e
    })
  inflight.set(key, p)
  return p
}

/** 配列をまとめて解決する。順序は維持。 */
export const resolveAttachmentUrls = (srcs: Array<string | undefined | null>): Promise<string[]> =>
  Promise.all((srcs || []).map(s => resolveAttachmentUrl(s)))

/** 解決済み URL を返す React hook（解決前は ""）。 */
export const useAttachmentUrl = (src: string | undefined | null): string => {
  const initial = !src || !isAttachmentEndpoint(src) ? src || "" : ""
  const [resolved, setResolved] = React.useState<string>(initial)
  React.useEffect(() => {
    let active = true
    resolveAttachmentUrl(src)
      .then(u => {
        if (active) setResolved(u)
      })
      .catch(() => {
        if (active) setResolved("")
      })
    return () => {
      active = false
    }
  }, [src])
  return resolved
}

/** <img> 互換。src に添付エンドポイント URL を渡すと自動解決して描画する。 */
export const AttachmentImage: React.FC<React.ImgHTMLAttributes<HTMLImageElement>> = props => {
  const { src, ...rest } = props
  const resolved = useAttachmentUrl(typeof src === "string" ? src : "")
  if (!resolved) {
    return null
  }
  return <img src={resolved} {...rest} />
}

/** <iframe> 互換（PDF プレビュー等）。 */
export const AttachmentIframe: React.FC<React.IframeHTMLAttributes<HTMLIFrameElement>> = props => {
  const { src, ...rest } = props
  const resolved = useAttachmentUrl(typeof src === "string" ? src : "")
  if (!resolved) {
    return null
  }
  return <iframe src={resolved} {...rest} />
}

/** 添付ファイルを別タブ等で開く（onClick ハンドラ用）。 */
export const openAttachment = (src: string | undefined | null, target: string = "_blank"): void => {
  resolveAttachmentUrl(src)
    .then(u => {
      if (u) window.open(u, target)
    })
    .catch(() => {})
}

type AttachmentLinkProps = React.AnchorHTMLAttributes<HTMLAnchorElement> & {
  href: string | undefined | null
}

/** <a> 互換。クリック時に解決してから開く（href は描画時には埋め込まない）。 */
export const AttachmentLink: React.FC<AttachmentLinkProps> = props => {
  const { href, target, onClick, children, ...rest } = props
  const handle = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault()
    if (onClick) onClick(e)
    openAttachment(href, target || "_blank")
  }
  return (
    <a href="#" onClick={handle} {...rest}>
      {children}
    </a>
  )
}
